/**
 * Application root.
 *
 * `App()` declares a top-level `<Routes>` with exactly one PUBLIC route —
 * `/personvern` (the privacy notice) — and everything else behind the
 * `AuthGate` authentication boundary (login / token / /me). Once the caller is
 * authenticated and provisioned, `AuthGate` hands the resolved `MeResponse` to
 * `AuthenticatedApp`, which sets up the cookbook context-switcher and the
 * routed product UI:
 *   /              → home feed (recipe list for the active cookbook)
 *   /recipes/:id   → recipe read view (sections + global step numbers)
 *   /cookbooks/:cookbookId/manage → owner-only sharing/members (P2-F2)
 *   /callback, *   → fall back to the feed (the OIDC code exchange already
 *                    happened; onSigninCallback rewrote the URL to "/").
 *
 * The router lives in main.tsx (BrowserRouter); here we only declare routes.
 */

import type { MeResponse } from "@tjommi/shared";
import React from "react";
import { useAuth } from "react-oidc-context";
import { Link, Navigate, Route, Routes } from "react-router-dom";

import { AuthGate } from "./auth/AuthGate";
import { TopBar } from "./components/TopBar";
import { CookbookProvider } from "./cookbook/CookbookContext";
import { AdminPage } from "./pages/AdminPage";
import { CookbookManagePage } from "./pages/CookbookManagePage";
import { IdpLinkCallbackPage } from "./pages/IdpLinkCallbackPage";
import { HomePage } from "./pages/HomePage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { CookModePage } from "./pages/CookModePage";
import { RecipeDetailPage } from "./pages/RecipeDetailPage";
import { RecipeEditorPage } from "./pages/RecipeEditorPage";

function AuthenticatedApp({ me }: { me: MeResponse }): React.JSX.Element {
  const auth = useAuth();
  const accessToken = auth.user?.access_token ?? "";
  const onSessionExpired = (): void => void auth.signinRedirect();

  // Closed-pilot approval gate: a self-registered but not-yet-approved caller
  // never reaches the product UI or fetches product data — /me is the ONLY
  // request an unapproved user's client makes (everything else 403s server-
  // side anyway; this is purely the calm-UX mirror of that rule).
  if (!me.approved) {
    return (
      <div className="center-screen">
        <section className="auth-card">
          <h1>
            deSjeff
            <span className="dot" />
          </h1>
          <h2>Venter på godkjenning</h2>
          <p data-testid="pending-approval">
            Kontoen din er registrert og venter på at en administrator godkjenner den. Du får
            tilgang så snart den er godkjent.
          </p>
          <Link to="/personvern" className="link-btn" data-testid="privacy-link">
            Personvern
          </Link>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginTop: "14px" }}
            data-testid="logout-button"
            onClick={() => void auth.signoutRedirect()}
          >
            Logg ut
          </button>
        </section>
      </div>
    );
  }

  // /me provisioning guarantees a primary cookbook on first login, so this is a
  // defensive guard, not an expected state — render a calm message over a crash.
  if (me.cookbooks.length === 0) {
    return (
      <div className="center-screen">
        <section className="auth-card">
          <h1>Ingen kokebok</h1>
          <p data-testid="no-cookbooks">
            Kontoen din har ingen kokebok ennå. Last siden på nytt — den opprettes automatisk ved
            innlogging.
          </p>
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="logout-button"
            onClick={() => void auth.signoutRedirect()}
          >
            Logg ut
          </button>
        </section>
      </div>
    );
  }

  return (
    <CookbookProvider cookbooks={me.cookbooks} userId={me.subject}>
      <Routes>
        {/* Cook mode is a full-screen, distraction-free kitchen view — it renders
            OUTSIDE the app shell (no TopBar) so it can own the whole viewport. */}
        <Route
          path="/recipes/:id/cook"
          element={<CookModePage accessToken={accessToken} onSessionExpired={onSessionExpired} />}
        />
        <Route
          path="*"
          element={
            <div className="app">
              <TopBar
                me={me}
                accessToken={accessToken}
                onLogout={() => void auth.signoutRedirect()}
                onSessionExpired={onSessionExpired}
              />
              <Routes>
                <Route
                  path="/"
                  element={
                    <HomePage
                      accessToken={accessToken}
                      userId={me.subject}
                      onSessionExpired={onSessionExpired}
                    />
                  }
                />
                {/* "Koble til <provider>" return route — the SPA-owned successUrl/
                    failureUrl for CONNECT-GOOGLE-SPEC.md Path A, generalized to
                    any configured provider via identity/providers.ts. */}
                <Route
                  path="/settings/connections/:provider/callback"
                  element={
                    <IdpLinkCallbackPage
                      subject={me.subject}
                      accessToken={accessToken}
                      onSessionExpired={onSessionExpired}
                    />
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <AdminPage
                      isAdmin={me.isAdmin}
                      accessToken={accessToken}
                      onSessionExpired={onSessionExpired}
                    />
                  }
                />
                <Route
                  path="/cookbooks/:cookbookId/manage"
                  element={
                    <CookbookManagePage
                      accessToken={accessToken}
                      onSessionExpired={onSessionExpired}
                    />
                  }
                />
                <Route
                  path="/recipes/new"
                  element={
                    <RecipeEditorPage
                      mode="new"
                      accessToken={accessToken}
                      onSessionExpired={onSessionExpired}
                    />
                  }
                />
                <Route
                  path="/recipes/:id/edit"
                  element={
                    <RecipeEditorPage
                      mode="edit"
                      accessToken={accessToken}
                      onSessionExpired={onSessionExpired}
                    />
                  }
                />
                <Route
                  path="/recipes/:id"
                  element={
                    <RecipeDetailPage
                      accessToken={accessToken}
                      me={me}
                      onSessionExpired={onSessionExpired}
                    />
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          }
        />
      </Routes>
    </CookbookProvider>
  );
}

export function App(): React.JSX.Element {
  return (
    <Routes>
      {/* Public — reachable without login (no AuthGate). */}
      <Route path="/personvern" element={<PrivacyPage />} />
      {/* Everything else stays behind the auth boundary, exactly as before. */}
      <Route path="*" element={<AuthGate>{(me) => <AuthenticatedApp me={me} />}</AuthGate>} />
    </Routes>
  );
}
